import { type FlowNodeResultADT } from '../../types/flow'
import type { BaseData, FlowExecutorNode } from './type'

/**
 * 链表结构的通用流执行器
 *
 * @export
 * @class FlowExecutor
 * @typedef {FlowExecutor}
 * @template {BaseData} T
 */
export class FlowExecutor<T extends BaseData> {
    readonly #visited = new Set<string>()

    readonly #finished = new Set<string>()

    readonly #pendingQueue: string[] = []

    readonly #results: Map<string, FlowNodeResultADT> = new Map()

    #taskQueue: FlowExecutorNode<T>[] = []

    constructor(
        private readonly nodes: Map<string, FlowExecutorNode<T>>,
        startNodes: string[],
        private readonly taskExecutor: (node: FlowExecutorNode<T>) => Promise<FlowNodeResultADT>
    ) {
        this.nodes = nodes
        this.#pendingQueue = [...startNodes]
        this.taskExecutor = taskExecutor
    }

    getResults() {
        return this.#results
    }

    async run() {
        while (this.#pendingQueue.length > 0) {
            const id = this.#pendingQueue.shift()
            if (!id) {
                continue
            }

            const node = this.nodes.get(id)
            if (!node || this.#visited.has(id)) {
                continue
            }

            // 如果有前置未完成则等待
            if (node.left && !node.left.every(item => this.#finished.has(item))) {
                continue
            }

            this.#visited.add(id)
            this.#taskQueue.push(node)
        }

        if (this.#taskQueue.length > 0) {
            await this.#executeTaskQueue()
        }
    }

    #executeTaskQueue() {
        const currentQueue = this.#taskQueue
        this.#taskQueue = []

        return Promise.allSettled(
            currentQueue.map(async node => {
                const result = await this.taskExecutor(node)
                this.#results.set(node.id, result)
                this.#finished.add(node.id)

                if (!node.right) {
                    return
                }

                this.#pendingQueue.push(...node.right)
                await this.run()
            })
        )
    }
}
